// #genai Access-denied screen shown to non-EV guest users whose token exists but
// whose account has not been granted access to the shared resource.
//
// Layout: re-uses AuthSplitLayout (aerial bg + white panel) to stay visually consistent
// with the rest of the sign-in / guest flow.
//
// Behaviour
// ─────────
// • "Switch accounts" — clears the current session token and redirects to /signin
//   so a different account can authenticate.
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@eagleview/ev-comp-library';
import {
  getUserEmailFromToken, signOutUser, isOktaToken, getAuthToken,
} from 'utils/app.utils';
import AuthSplitLayout, { SplitLayoutFooter } from 'features/auth-split-layout';
import { LOCAL_STORAGE_ITEMS, SESSION_STORAGE_ITEMS } from 'constants/AppConstants';
import { guestFlowBaseStyles, guestFlowThemedStyles } from './guestFlow.styles';
import { GUEST_ROUTES } from '../../constants/GuestAccess';

const useStyles = makeStyles((theme) => {
  const themed = guestFlowThemedStyles(theme);
  return {
    ...guestFlowBaseStyles,
    ...themed,
    content: { marginTop: '20px' },
    signedInRow: { ...guestFlowBaseStyles.bodySmall, margin: '24px 0 0 0' },
    switchLink: { ...themed.actionLink, display: 'block', marginTop: '8px' },
  };
});

// #genai
const AccessDenied = ({ userEmail, redirectUrl }) => {
  const classes = useStyles();

  const displayEmail = useMemo(() => userEmail || getUserEmailFromToken(), [userEmail]);

  const signinUrl = redirectUrl
    ? `${GUEST_ROUTES.SIGNIN_REDIRECT_PREFIX}${encodeURIComponent(redirectUrl)}`
    : GUEST_ROUTES.SIGNIN;

  const handleSwitchAccounts = () => {
    if (isOktaToken(getAuthToken())) {
      // EV user — proper Okta logout (clears Okta session + local tokens). #genai
      signOutUser();
      return;
    }
    // Guest user — clear tokens and redirect directly to /signin?redirect=<resource>
    // so the guest sign-in flow restarts with the correct resource pre-filled. #genai
    SESSION_STORAGE_ITEMS.forEach((key) => sessionStorage.removeItem(key));
    LOCAL_STORAGE_ITEMS.forEach((key) => localStorage.removeItem(key));
    window.location.assign(signinUrl);
  };

  return (
    <AuthSplitLayout footer={<SplitLayoutFooter />} sidebarWidth="509px">
      <h1 className={classes.title}>You Need Access</h1>
      <div className={classes.content}>
        <p className={classes.bodySmall}>
          Your account does not have access to view this resource.
          Want in? Switch to an account with access.
        </p>

        {displayEmail && (
          <p className={classes.signedInRow}>
            You are signed in as
            {' '}
            <span className={classes.boldText}>{displayEmail}</span>
          </p>
        )}

        <button
          type="button"
          className={classes.switchLink}
          onClick={handleSwitchAccounts}
        >
          Switch accounts
        </button>
      </div>
    </AuthSplitLayout>
  );
};

AccessDenied.propTypes = {
  userEmail: PropTypes.string,
  redirectUrl: PropTypes.string,
};

AccessDenied.defaultProps = {
  userEmail: '',
  redirectUrl: '',
};

export default AccessDenied;
